import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, Card, CardHeader, CardContent, CardActions, IconButton, TextField } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { stateListAtom, userInformationAtom } from '../../Services/_states';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { ConstantActions } from '../../Services/_actions/constant.actions.js';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataNotFound from '../Shared/DataNotFound';
import { OrganisationActions } from '../../Services/_actions/Organisation/organisation.actions';
import ModalForms from '../Shared/Modals/index.js';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { useSetRecoilState } from 'recoil';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';

const UpdateGst1 = () => {
    const [selectedOrg, setSelectedOrg] = useState(null);
    const actions = ConstantActions();
    const organisationActions = OrganisationActions();
    const loggedInUser = useRecoilValue(userInformationAtom);
    const [organisationList, setOrganisationList] = useState([]);
    const stateList = useRecoilValue(stateListAtom);
    const setStateList = useSetRecoilState(stateListAtom);
    const [cityList, setCityList] = useState([]);
    const [areaList, setAreaList] = useState([]);
    const [gstList, setGstList] = useState([]);
    const [isModalShow, setIsModalShow] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddFetched, setIsAddFetched] = useState(false);
    const { showSnackBar } = useSnackBar();

    const [formData, setFormData] = useState({
        company_id: '',
        state_id: '',
        city_id: '',
        area: '',
        address: '',
        pincode: '',
        gst_number: '',
        is_primary_gst: '',
    });

    const handleStateChange = async (e) => {
        setFormData({ ...formData, state_id: e?.state_code || '' });
        if (e?.id) {
            const cities = await actions.getCityByState(e?.id);
            setCityList(cities || []);
        } else {
            setCityList([]);
        }
    };
    const onSelectOrganisation = (selectedOrg) => {
        setSelectedOrg(selectedOrg);
        setFormData((prev) => ({
            ...prev,
            company_id: selectedOrg?.organisation_id || '',
        }));

        if (selectedOrg?.organisation_id) {
            getCompanyGst({ organisation_id: selectedOrg.organisation_id });
        }
    };

    const getCompanyGst = async ({ organisation_id }) => {
        const res = await organisationActions.getCompanyGst(organisation_id);
        if (res.status === 200) {
            setGstList(res.data);
            setIsAddFetched(true);
        } else {
            showSnackBar('An error occurred while fetching GST data.', 'error');
        }
    };



    const handleEditGst = async (data) => {
        setFormData({ ...data });
        setIsModalShow(true);
        setIsEditMode(true);

        try {
            let stateId = stateList?.find((item) => item?.state_code === data?.state_id)?.id;
            if (stateId) {
                const cities = await actions.getCityByState(stateId);
                setCityList(cities || []);
            }
            const areas = await actions.getAreaByCity(data?.city_id);
            setAreaList(areas || []);
        } catch (error) {
            setAreaList([]);
            setCityList([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missingFields = [];
        if (!formData.company_id) missingFields.push('Company');
        if (!formData.state_id) missingFields.push('State');
        if (!formData.city_id) missingFields.push('City');
        if (!formData.is_primary_gst) missingFields.push('GST Type');
        if (!formData.area) missingFields.push('Area');

        // If there are missing fields, show an error message
        if (missingFields.length > 0) {
            showSnackBar(`Please fill in the following fields: ${missingFields.join(', ')}`, 'error');
            return;
        }

        try {
            const res = await organisationActions.createCompanyGst(formData);
            if (res.status === 200) {
                if (formData?.address_id) {
                    setGstList((prevAddresses) =>
                        prevAddresses.map((address) =>
                            address.address_id === formData?.address_id ? { ...address, ...formData } : address
                        )
                    );
                } else {
                    getCompanyGst({ organisation_id: formData?.company_id });
                }

                setFormData({
                    ...formData,
                    state_id: '',
                    city_id: '',
                    area: '',
                    address: '',
                    pincode: '',
                    gst_number: '',
                    is_primary_gst: '',
                });
                setIsModalShow(false);
                showSnackBar('GST Details Updated successfully.', 'success');
            } else {
                showSnackBar('Failed to update GST details. Please try again.', 'error');
            }
        } catch (error) {
            showSnackBar('An error occurred while submitting the GST details.', 'error');
        }
    };

    const handleCityChange = async (selectedCity) => {
        setFormData({ ...formData, city_id: selectedCity?.id || '' });
        if (selectedCity?.id) {
            try {
                const areas = await actions.getAreaByCity(selectedCity?.id);
                setAreaList(areas || []);
            } catch (error) {
                setAreaList([]);
            }
        } else {
            setAreaList([]);
        }
    };

    const handleDeleteGst = async (data) => {
        try {
            const res = await organisationActions.deleteCompanyGst(data);
            if (res?.status === 200) {
                setGstList(gstList?.filter((detail) => detail.address_id !== data?.address_id));
                showSnackBar('GST details deleted successfully.', 'success');
            } else
                showSnackBar('Failed to delete GST details. Please try again.', 'error');
        } catch (error) {
            showSnackBar('An error occurred while deleting the GST details.', 'error');
        }
    };

    useEffect(() => {
        const fetchStates = async () => {
            const res = await actions.getStateList();
            if (res?.status === 200 && Array.isArray(res.data)) {
                setStateList(res.data);
            } else {
                console.log('Failed to fetch state list');
            }
        };

        fetchStates();
    }, []);

    useEffect(() => {
        const loadOrganisationsAndStates = async () => {
            if (loggedInUser?.is_superuser) {
                let res = await actions.getOrganisations();
                setOrganisationList(res || []);

            } else {
                setFormData({
                    ...formData,
                    company_id: loggedInUser?.profile?.organisation?.organisation_id,
                });
                getCompanyGst(loggedInUser?.profile?.organisation);
            }
            await actions.getStateList();
        };
        loadOrganisationsAndStates();
        console.log(loggedInUser);
    }, []);

    return (
        <Box>
            <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
                Update GST Details
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                    {loggedInUser?.is_superuser && (
                        <Box sx={{ maxWidth: 210 }}>
                            <OrganisationDropdown
                                value={selectedOrg}
                                onChange={onSelectOrganisation}
                                label="Select Company"
                            />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                    {formData?.company_id && (
                        <Button
                            size='small'
                            variant="contained"
                            onClick={() => {
                                setIsModalShow(true);
                                setIsEditMode(false);
                                setFormData(
                                    Object.fromEntries(
                                        Object.entries(formData).map(([key, value]) => [
                                            key,
                                            key === 'company_id' ? value : '',
                                        ])
                                    )
                                );
                            }}
                        >
                            Add
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isAddFetched && (
                <Grid container spacing={2}>
                    {gstList?.length > 0 ? (
                        gstList.map((gstAddress, index) => (
                            <Grid item md={3} xs={12} key={index}>
                                <Card sx={{ height: '100%' }}>
                                    <CardHeader title={`GST ${index + 1}`} />
                                    <CardContent>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Company ID:</strong> {gstAddress?.company_id}
                                            <br />
                                            <strong>State:</strong> {gstAddress?.state_id}
                                            <br />
                                            <strong>City:</strong> {gstAddress?.city_id}
                                            <br />
                                            <strong>Area:</strong> {gstAddress?.area}
                                            <br />
                                            <strong>Address:</strong> {gstAddress?.address}
                                            <br />
                                            <strong>Pincode:</strong> {gstAddress?.pincode}
                                            <br />
                                            <strong>GST Number:</strong> {gstAddress?.gst_number}
                                            <br />
                                            <strong>Is Primary:</strong> {gstAddress?.is_primary_gst ? 'Yes' : 'No'}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditGst(gstAddress)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteGst(gstAddress)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    ) : (
                        <DataNotFound message="No GST found." />
                    )}
                </Grid>
            )}

            {isModalShow && (
                <ModalForms
                    show={isModalShow}
                    handleClose={() => setIsModalShow(false)}
                    modalHeader={`${isEditMode ? 'Edit' : 'Create'} GST Details`}
                    modalId={'address-modal'}
                    size="md"
                    modalBody={
                        <form onSubmit={handleSubmit}>
                            <Box gap={2} sx={{ width: { xs: '100%', sm: 'auto' }, mb: { xs: 2, sm: 3 }, display: { xs: 'block', sm: 'flex' } }}>
                                <Box flex={1} sx={{ mb: { xs: 1, sm: 0 } }}>
                                    <Typography>Select State</Typography>
                                    <SearchableDropdown
                                        options={stateList || []}
                                        labelKey="state_name"
                                        valueKey="state_code"
                                        label="Select State"
                                        value={
                                            stateList?.find((item) => item?.state_code === formData?.state_id) || null
                                        }
                                        onChange={(selected) => handleStateChange(selected)}
                                        disabled={isEditMode}
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography>Select City</Typography>
                                    <SearchableDropdown
                                        options={cityList || []}
                                        labelKey="city_name"
                                        valueKey="id"
                                        label="Select City"
                                        value={
                                            cityList?.find((item) => item?.id === formData?.city_id) || null
                                        }
                                        onChange={(selected) => handleCityChange(selected)}
                                        disabled={isEditMode}
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                            </Box>

                            <Box display="flex" gap={2} sx={{ width: { xs: '100%', sm: 'auto', mb: { xs: 2, sm: 3 } }, display: { xs: 'block', sm: 'flex' } }}>
                                <Box flex={1} sx={{ mb: { xs: 1, sm: 0 } }}>
                                    <Typography>Select Area</Typography>
                                    <SearchableDropdown
                                        options={areaList || []}
                                        labelKey="label"
                                        valueKey="area_code"
                                        label="Select Area"
                                        value={
                                            areaList?.find((item) => item?.area_code === formData?.area) || null
                                        }
                                        onChange={(selected) =>
                                            setFormData({ ...formData, area: selected?.area_code || '' })
                                        }
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                                <Box flex={1} sx={{ mb: { xs: 1, sm: 0 } }}>
                                    <Typography>GST Type</Typography>
                                    <SearchableDropdown
                                        options={[
                                            { label: 'Primary', value: 'primary' },
                                            { label: 'Secondary', value: 'secondary' },
                                        ]}
                                        labelKey="label"
                                        valueKey="value"
                                        label="GST Type"
                                        value={
                                            formData?.is_primary_gst
                                                ? {
                                                    label:
                                                        formData.is_primary_gst === 'primary'
                                                            ? 'Primary'
                                                            : 'Secondary',
                                                    value: formData.is_primary_gst,
                                                }
                                                : null
                                        }
                                        onChange={(selected) =>
                                            setFormData({ ...formData, is_primary_gst: selected?.value || '' })
                                        }
                                        size="small"
                                        sx={{ mt: "5px" }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: { xs: 2, sm: 3 }, mt: { sx: 0, sm: 2 } }}>
                                <Typography>GST Number</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    sx={{ mt: "5px" }}
                                    type="text"
                                    required
                                    value={formData?.gst_number || ''}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            gst_number: e.target.value.toUpperCase(),
                                        })
                                    }
                                    inputProps={{
                                        maxLength: 15,
                                        pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
                                    }}
                                    helperText="Example GST numbers: 27AAEPM1234A1Z5, 07AACCM9876F1Z2"
                                    FormHelperTextProps={{
                                        sx: { ml: "2px" }
                                    }}
                                />
                            </Box>

                            <Box gap={2} sx={{ width: { xs: '100%', sm: 'auto' }, mb: { xs: 2, sm: 3 }, display: { xs: 'block', sm: 'flex' } }}>
                                <Box flex={1}>
                                    <Typography>Address</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        sx={{ mt: "5px" }}
                                        type="text"
                                        required
                                        value={formData?.address || ''}
                                        onChange={(e) =>
                                            setFormData({ ...formData, address: e.target.value })
                                        }
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Typography>Pincode</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        sx={{ mt: "5px" }}
                                        type="text"
                                        required
                                        value={formData?.pincode || ''}
                                        onChange={(e) =>
                                            setFormData({ ...formData, pincode: e.target.value })
                                        }
                                        inputProps={{
                                            maxLength: 6,
                                            pattern: '[0-9]{6}',
                                        }}
                                        helperText="Please enter a valid 6-digit pincode"
                                        FormHelperTextProps={{
                                            sx: { ml: "2px" }
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Button size='small' type="submit" variant="contained" color="primary">
                                Submit
                            </Button>
                        </form>
                    }
                />
            )}


        </Box>
    );
};

export default UpdateGst1;
